/**
 * GlobalRevisionCard - Reusable component for displaying individual global revisions
 * Following modular architecture pattern from existing components
 */

import { useState } from 'react';
import { Calendar, User, Tag, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import GlobalRevisionsService from '../../utils/globalRevisionsService.js';

// Utility function to decode HTML entities and convert to proper line breaks
const decodeHtmlEntities = (text) => {
    if (!text) return '';

    // Create a temporary element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    let decoded = textarea.value;

    // Convert common HTML entities to proper characters - more comprehensive list
    decoded = decoded
        .replace(/&#13;&#10;/g, '\n')  // CRLF
        .replace(/&#10;/g, '\n')      // LF
        .replace(/&#13;/g, '\n')      // CR
        .replace(/&amp;/g, '&')       // &
        .replace(/&lt;/g, '<')        // <
        .replace(/&gt;/g, '>')        // >
        .replace(/&quot;/g, '"')      // "
        .replace(/&#39;/g, "'")       // '
        .replace(/&nbsp;/g, ' ')      // non-breaking space
        .replace(/&#8226;/g, '•')     // bullet point
        .replace(/&#38;/g, '&')       // & (alternative)
        .replace(/&#34;/g, '"')       // " (alternative)
        .replace(/&#8211;/g, '–')     // en dash
        .replace(/&#8212;/g, '—')     // em dash
        .replace(/&#8220;/g, '"')     // left double quotation mark
        .replace(/&#8221;/g, '"')     // right double quotation mark
        .replace(/&#8217;/g, "'")     // right single quotation mark
        .replace(/&#8216;/g, "'");    // left single quotation mark

    return decoded;
};

export default function GlobalRevisionCard({ revision, variant = 'default', showClient = true }) {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!revision) {
        return null;
    }

    const {
        client,
        revision: revisionText,
        date,
        status,
        type,
        isActive,
        isRecent
    } = revision;

    // Get styling based on revision type
    const typeColorClass = GlobalRevisionsService.getRevisionTypeColor(type);
    const typeIcon = GlobalRevisionsService.getRevisionTypeIcon(type);
    const formattedDate = GlobalRevisionsService.formatRevisionDate(date);
    const isRecentFlag = GlobalRevisionsService.isRecentRevision(date);

    // Variant styling
    const getVariantClasses = () => {
        switch (variant) {
            case 'compact':
                return 'p-3 h-full flex flex-col';
            case 'detailed':
                return 'p-6';
            default:
                return 'p-4';
        }
    };

    // Get text limit based on variant
    const getTextLimit = () => {
        switch (variant) {
            case 'compact':
                return 120; // Shorter text for compact horizontal cards
            case 'detailed':
                return null; // No limit for detailed view
            default:
                return 200;
        }
    };

    // Show text based on variant with HTML entity decoding
    const getRevisionText = () => {
        const decoded = decodeHtmlEntities(revisionText) || 'No revision text available';
        const textLimit = getTextLimit();

        // If expanded or no limit, return full text
        if (isExpanded || !textLimit) {
            return decoded;
        }

        // Return truncated text if over limit
        if (decoded.length > textLimit) {
            return decoded.substring(0, textLimit) + '...';
        }

        return decoded;
    };

    // Check if text is truncated
    const isTruncated = () => {
        const decoded = decodeHtmlEntities(revisionText) || '';
        const textLimit = getTextLimit();
        return textLimit && decoded.length > textLimit;
    };

    return (
        <div className={`
            bg-white rounded-lg border border-slate-200 hover:border-orange-200
            transition-all duration-200 hover:shadow-md shadow-sm ${getVariantClasses()}
            group
        `}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                    {/* Client Name */}
                    {showClient && (
                        <div className="flex items-center gap-2">
                            <User size={16} className="text-slate-500" />
                            <span className="font-medium text-slate-900 text-sm">
                                {client || 'Unknown Client'}
                            </span>
                        </div>
                    )}

                    {/* Recent Badge */}
                    {isRecentFlag && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Recent
                        </span>
                    )}
                </div>

                {/* Status Indicator */}
                <div className="flex items-center gap-2">
                    {isActive ? (
                        <div className="w-2 h-2 bg-green-500 rounded-full" title="Active" />
                    ) : (
                        <div className="w-2 h-2 bg-slate-400 rounded-full" title="Inactive" />
                    )}
                </div>
            </div>

            {/* Revision Content */}
            <div className={`mb-3 ${variant === 'compact' ? 'flex-1' : ''}`}>
                <p className={`text-slate-700 leading-relaxed ${variant === 'compact' ? 'text-xs' : 'text-sm'} whitespace-pre-wrap transition-all duration-300`}>
                    {getRevisionText()}
                </p>

                {/* Expand/Collapse Button */}
                {isTruncated() && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="mt-2 flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp size={14} />
                                View less
                            </>
                        ) : (
                            <>
                                <ChevronDown size={14} />
                                View more
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Footer */}
            <div className={`flex items-center ${variant === 'compact' ? 'flex-col gap-2' : 'justify-between'} text-xs text-slate-500 pt-2 border-t border-slate-100`}>
                {/* Date */}
                <div className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-slate-400" />
                    <span className="font-medium">{formattedDate}</span>
                </div>

                {/* Type Badge */}
                <div className={`
                    flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold
                    ${typeColorClass} transition-all duration-200
                `}>
                    <span>{typeIcon}</span>
                    <span className="capitalize">{type || 'General'}</span>
                </div>
            </div>

            {/* Compliance Terms (Detailed variant only) */}
            {variant === 'detailed' && revision.complianceTerms && revision.complianceTerms.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                        <Tag size={14} className="text-slate-500" />
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                            Compliance Terms
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {revision.complianceTerms.slice(0, 3).map((term, index) => (
                            <span
                                key={index}
                                className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                            >
                                {GlobalRevisionsService.truncateRevisionText(term, 20)}
                            </span>
                        ))}
                        {revision.complianceTerms.length > 3 && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded">
                                +{revision.complianceTerms.length - 3} more
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Warning for inactive revisions */}
            {!isActive && variant !== 'compact' && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle size={14} />
                        <span className="text-xs">This revision is no longer active</span>
                    </div>
                </div>
            )}
        </div>
    );
}
